'use client'

import type { ChatExperience } from '@dupe/common/chatExperience'
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react'

export type { ChatExperience }

const ChatExperienceContext = createContext<ChatExperience | null>(null)
const SetChatExperienceContext = createContext<
  ((next: ChatExperience) => void) | null
>(null)

/**
 * Supplies the `chat_experience` value `useAnalytics` stamps on every event
 * fired beneath it.
 *
 * ⚠️ The value is STATE, not just the `value` prop, and that is the whole
 * point of this file (DUP-2336). `value` is the client generation — what the
 * browser knows at mount, always `'v2'` here. Which ENGINE serves a turn is
 * decided server-side per turn and reported back on the `serving_engine` wire
 * frame, so BrainChat calls `useSetChatExperience()` when that frame lands and
 * every later event in the session carries the true value.
 *
 * ⛔ Do not collapse this back to a plain prop. The browser cannot derive the
 * `'v3'` case — see `@dupe/common/chatExperience`.
 */
export function ChatExperienceProvider({
  value,
  children,
}: {
  value: ChatExperience
  children: ReactNode
}) {
  const [experience, setExperience] = useState<ChatExperience>(value)

  // Follow the prop if the mount point ever changes it. Without this the very
  // first `value` would win forever, which would be a silent trap for anyone
  // who later makes the prop dynamic.
  const previousValue = useRef(value)
  useEffect(() => {
    if (previousValue.current !== value) {
      previousValue.current = value
      setExperience(value)
    }
  }, [value])

  const set = useCallback((next: ChatExperience) => {
    setExperience((current) => (current === next ? current : next))
  }, [])

  return (
    <ChatExperienceContext.Provider value={experience}>
      <SetChatExperienceContext.Provider value={set}>
        {children}
      </SetChatExperienceContext.Provider>
    </ChatExperienceContext.Provider>
  )
}

export function useChatExperience(): ChatExperience | null {
  return useContext(ChatExperienceContext)
}

/**
 * Report the engine that actually served a turn. Returns null outside the
 * provider, so callers on non-chat surfaces are a no-op rather than a crash.
 */
export function useSetChatExperience():
  | ((next: ChatExperience) => void)
  | null {
  return useContext(SetChatExperienceContext)
}
